import { useEffect, useRef } from 'react';
import { ArrowDown, MapPin, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import HeroSlider from '@/components/HeroSlider';

export default function Hero() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      
      const x = (clientX / innerWidth - 0.5) * 20;
      const y = (clientY / innerHeight - 0.5) * 20;
      
      heroRef.current.style.setProperty('--mouse-x', `${x}px`);
      heroRef.current.style.setProperty('--mouse-y', `${y}px`);
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToMenu = () => {
    const menuSection = document.querySelector('#menu');
    if (menuSection) {
      menuSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      ref={heroRef}
      className="relative min-h-screen flex flex-col overflow-hidden"
      style={{
        background: `
          radial-gradient(ellipse at 20% 80%, rgba(255, 215, 0, 0.08) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 20%, rgba(220, 20, 60, 0.08) 0%, transparent 50%),
          linear-gradient(180deg, #000000 0%, #0a0a0a 100%)
        `,
      }}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        {/* Floating Orbs */}
        <div 
          className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full opacity-20 animate-float"
          style={{
            background: 'radial-gradient(circle, rgba(255, 215, 0, 0.3) 0%, transparent 70%)',
            filter: 'blur(40px)',
            transform: 'translate(var(--mouse-x, 0), var(--mouse-y, 0))',
            transition: 'transform 0.3s ease-out',
          }}
        />
        <div 
          className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-20 animate-float"
          style={{
            background: 'radial-gradient(circle, rgba(220, 20, 60, 0.3) 0%, transparent 70%)',
            filter: 'blur(50px)',
            animationDelay: '1s',
            transform: 'translate(calc(var(--mouse-x, 0) * -1), calc(var(--mouse-y, 0) * -1))',
            transition: 'transform 0.3s ease-out',
          }}
        />

        {/* Korean Text Decoration */}
        <div className="absolute top-20 right-10 text-6xl font-bold text-[#FFD700]/5 rotate-12 select-none z-0">
          서울
        </div>
        <div className="absolute bottom-32 left-10 text-5xl font-bold text-[#DC143C]/5 -rotate-12 select-none z-0">
          한국
        </div>
      </div>

      {/* Hero Slider - Top Section */}
      <div className="relative z-10 w-full h-[50vh] sm:h-[55vh] md:h-[60vh]">
        <HeroSlider />
      </div>

      {/* Content Section - Bottom */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/30 mb-6 animate-glow">
          <span className="w-2 h-2 bg-[#FFD700] rounded-full animate-pulse" />
          <span className="text-sm text-[#FFD700] font-medium">Now Open for Pickup Orders</span>
        </div>

        {/* Tagline */}
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 font-['Orbitron'] text-center">
          <span className="gradient-text">Urban Korean</span>{' '}
          <span className="text-white">Urban Korean Food</span>
        </h2>

        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-6 text-center">
          Experience the vibrant flavors of Seoul right here in Kissimmee. 
          Authentic Korean taste with an urban twist.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          <Button
            size="lg"
            onClick={scrollToMenu}
            className="btn-neon text-black px-8 py-6 text-lg font-semibold rounded-xl"
          >
            Order Now
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              const aboutSection = document.querySelector('#about');
              if (aboutSection) {
                aboutSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="border-[#DC143C] text-[#DC143C] hover:bg-[#DC143C]/10 px-8 py-6 text-lg font-semibold rounded-xl"
          >
            Learn More
          </Button>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto">
          <div className="glass-card rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FFD700]/20 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-[#FFD700]" />
            </div>
            <div className="text-left">
              <p className="text-sm text-muted-foreground">Location</p>
              <p className="text-sm font-medium leading-tight">6083 W Irlo Bronson Memorial Hwy<br />Kissimmee, FL</p>
            </div>
          </div>
          <div className="glass-card rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#DC143C]/20 flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-[#DC143C]" />
            </div>
            <div className="text-left">
              <p className="text-sm text-muted-foreground">Pickup Time</p>
              <p className="text-sm font-medium">15-25 Minutes</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-bounce z-20">
        <ArrowDown className="w-6 h-6 text-[#FFD700]" />
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none z-0" />
    </section>
  );
}
